import { useState, useEffect, useCallback, useRef } from 'react';
import { cn, formatDate } from '../../lib/utils.js';
import { ChevronRight, FileText, MoreHorizontal, Trash2, Star, ChevronDown, Undo, Redo } from 'lucide-react';
import BlockEditor from './BlockEditor.jsx';

const EMOJIS = [
  // OS / Platforms
  '🐧', '🤖', '🍎', '🍏', '🪟', '💻', '🖥️', '📱', '⌚', '🕹️',
  // Servers / Network / Cloud
  '☁️', '🌩️', '🌐', '📡', '🔌', '🖧', '🗄️', '💾', '💽', '💿', '🔋', '📶', '🔗', 
  // Security / Auth / VPN
  '🔐', '🔒', '🔓', '🔑', '🗝️', '🛡️', '🆔', '🛂', '🎟️', '💳', '👁️‍🗨️', '🕵️', '👮',
  // Coding / Tools
  '⚙️', '🔧', '🛠️', '⌨️', '🖱️', '🚀', '⚡', '🔥', '🐛', '🐜', '🔨', '⛏️', '🧪',
  // Notes / Files / Data
  '📝', '📄', '📋', '📁', '📂', '🗂️', '📊', '📉', '📈', '🗑️', '✂️', '📎', '📚',
  // Office / Business / Misc
  '🏠', '🏢', '🌍', '🧭', '🔍', '📌', '📍', '🔖', '💼', '📅', '🕒', '⏳', 
  // Status / Alerts
  '✅', '❌', '⚠️', '🚫', '💡', '🔔', '📢', '💭', '💬',
  // Media / Leisure
  '📷', '🎬', '🎧', '🎮', '📧', '📦', '☕', '🍔', '🍕', '🎲'
];

const parseContent = (content) => {
  let parsedBlocks = [];
  if (content) {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed) && parsed.length > 0) {
        parsedBlocks = parsed;
      }
    } catch {
      // Fallback for plain text
    }
    
    if (parsedBlocks.length === 0 && content) {
      // Split plain text into blocks by newline
      parsedBlocks = content.split('\n').map(line => ({
        id: Math.random().toString(36).substr(2, 9),
        type: 'text',
        content: line
      }));
    }
  }
  
  if (parsedBlocks.length === 0) {
    parsedBlocks = [{ id: Math.random().toString(36).substr(2, 9), type: 'text', content: '', shouldFocus: true }];
  }
  return parsedBlocks;
};

const NoteEditor = ({ note, notes = [], folders, onSave, onDelete, onCreatePage, onNavigate }) => {
  const [prevNoteId, setPrevNoteId] = useState(note?.id);
  const [title, setTitle] = useState(note?.title || '');
  const [icon, setIcon] = useState(note?.icon || '📝');
  const [blocks, setBlocks] = useState(() => parseContent(note?.content));

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const titleRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const isInitialMount = useRef(true);

  // Undo/Redo State
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const skipHistoryRef = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Sync state with props when note changes
  if (note?.id !== prevNoteId) {
    setPrevNoteId(note.id);
    setTitle(note.title || '');
    setIcon(note.icon || '📝');
    setBlocks(parseContent(note.content));
    isInitialMount.current = true;
    
    // Reset History
    historyRef.current = [];
    historyIndexRef.current = -1;
    skipHistoryRef.current = false;
    setCanUndo(false);
    setCanRedo(false);

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
  }

  // Record History
  useEffect(() => {
    if (skipHistoryRef.current) {
      skipHistoryRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const snapshot = {
        title,
        icon,
        blocks: JSON.parse(JSON.stringify(blocks)) // Deep copy
      };

      // Check if actually changed from current history point
      const current = historyRef.current[historyIndexRef.current];
      if (current && 
          current.title === snapshot.title && 
          current.icon === snapshot.icon && 
          JSON.stringify(current.blocks) === JSON.stringify(snapshot.blocks)) {
        return;
      }

      // Add to history
      const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
      newHistory.push(snapshot);
      
      // Limit history size
      if (newHistory.length > 50) newHistory.shift();
      
      historyRef.current = newHistory;
      historyIndexRef.current = newHistory.length - 1;
      
      setCanUndo(historyIndexRef.current > 0);
      setCanRedo(false);
    }, 500); // 500ms debounce for history

    return () => clearTimeout(timer);
  }, [title, icon, blocks]);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      skipHistoryRef.current = true;
      historyIndexRef.current--;
      const prev = historyRef.current[historyIndexRef.current];
      
      setTitle(prev.title);
      setIcon(prev.icon);
      setBlocks(JSON.parse(JSON.stringify(prev.blocks)));
      
      setCanUndo(historyIndexRef.current > 0);
      setCanRedo(true);
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      skipHistoryRef.current = true;
      historyIndexRef.current++;
      const next = historyRef.current[historyIndexRef.current];
      
      setTitle(next.title);
      setIcon(next.icon);
      setBlocks(JSON.parse(JSON.stringify(next.blocks)));
      
      setCanUndo(true);
      setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
    }
  }, []);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      
      if (isMod && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
      } else if (isMod && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  const triggerSave = useCallback(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = setTimeout(() => {
      if (onSave && note) {
        onSave(note.id, {
          title: title || 'Untitled',
          icon,
          content: JSON.stringify(blocks.map(({ shouldFocus, ...b }) => b)),
        });
      }
    }, 1000); // 1s debounce for safety
  }, [title, icon, blocks, note, onSave]);

  useEffect(() => {
    if (note) {
      triggerSave();
    }
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [title, icon, blocks, triggerSave]);

  const handleTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const firstBlock = document.querySelector('[data-block-index="0"] textarea');
      if (firstBlock) firstBlock.focus();
    }
  }, []);

  const handleIconSelect = useCallback((emoji) => {
    setIcon(emoji);
    setShowEmojiPicker(false);
  }, []);

  const handleDelete = useCallback(() => {
    if (onDelete && note) {
      onDelete(note.id);
    }
  }, [onDelete, note]);

  const handleCreatePage = useCallback(() => {
    if (onCreatePage && note) {
      onCreatePage(note.id);
    }
  }, [onCreatePage, note]);

  if (!note) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-500">
        <FileText size={48} className="mb-4 opacity-50" />
        <p className="text-lg font-medium">Select a note to edit</p>
        <p className="text-sm">Or create a new one from the sidebar</p>
      </div>
    );
  }

  const breadcrumb = [];
  if (note.folder_id) {
    const folder = folders.find((f) => f.id === note.folder_id);
    if (folder) {
      breadcrumb.push({ id: folder.id, name: folder.name, icon: folder.icon });
    }
  }
  
  let currentParentId = note.parent_id;
  const parentChain = [];
  const visitedParents = new Set();
  while (currentParentId) {
    if (visitedParents.has(currentParentId)) {
      console.warn('[NoteEditor] Circular dependency detected in parent notes:', currentParentId);
      break;
    }
    visitedParents.add(currentParentId);
    const parentNote = notes.find(n => n.id === currentParentId);
    if (parentNote) {
      parentChain.unshift({ id: parentNote.id, name: parentNote.title || 'Untitled', icon: parentNote.icon });
      currentParentId = parentNote.parent_id;
    } else {
      break;
    }
  }
  breadcrumb.push(...parentChain);
  breadcrumb.push({ id: note.id, name: title || 'Untitled', icon });

  const childPages = notes.filter((n) => n.parent_id === note.id);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      console.log('Editor Width', editorRef.current.offsetWidth);
    }
  }, []);

  return (
    <div className="h-full overflow-y-auto" ref={editorRef}>
      <div className="w-full py-8 px-8">
        {/* Breadcrumb */}
        <div className="flex flex-wrap items-center gap-1 text-sm text-zinc-500 mb-6">
          {breadcrumb.map((item, index) => (
            <span key={item.id} className="flex items-center gap-1">
              {index > 0 && <ChevronRight size={12} />}
              <button
                onClick={() => onNavigate?.(item.id)}
                className="hover:text-zinc-300 transition flex items-center"
              >
                <span className="mr-1">{item.icon}</span>
                <span className="truncate max-w-[120px]">{item.name}</span>
              </button>
            </span>
          ))}
        </div>

        {/* Title Section */}
        <div className="flex items-start gap-4 mb-8">
          {/* Icon Picker */}
          <div className="relative shrink-0">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="text-5xl hover:bg-white/5 rounded-lg p-2 transition flex items-center justify-center min-w-[64px] min-h-[64px]"
              title="Change icon"
            >
              {icon}
            </button>
            
            {showEmojiPicker && (
              <div 
                className="absolute z-[100] top-full left-0 mt-2 p-3 rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl w-[220px] max-h-[320px] overflow-y-auto"
                style={{
                  boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.5), 0 8px 10px -6px rgb(0 0 0 / 0.5)'
                }}
              >
                <div className="grid grid-cols-4 gap-2">
                  {EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleIconSelect(emoji)}
                      className="text-2xl p-2 hover:bg-white/10 rounded-lg transition flex items-center justify-center"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Title Input */}
          <div className="flex-1 min-w-0 pt-3">
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              placeholder="Untitled"
              className="w-full text-4xl font-bold bg-transparent border-none outline-none text-zinc-100 placeholder:text-zinc-700 leading-tight"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 mt-2">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className={cn(
                "p-2 rounded-lg transition",
                canUndo ? "text-zinc-400 hover:bg-white/5 hover:text-zinc-200" : "text-zinc-700 cursor-not-allowed"
              )}
              title="Undo (Ctrl+Z)"
            >
              <Undo size={18} />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className={cn(
                "p-2 rounded-lg transition",
                canRedo ? "text-zinc-400 hover:bg-white/5 hover:text-zinc-200" : "text-zinc-700 cursor-not-allowed"
              )}
              title="Redo (Ctrl+Y)"
            >
              <Redo size={18} />
            </button>
            
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-2 hover:bg-white/5 rounded-lg transition text-zinc-500 hover:text-zinc-300"
              >
                <MoreHorizontal size={20} />
              </button>
              {showActions && (
                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-border bg-panel shadow-xl py-1 z-50">
                  <button
                    onClick={() => setShowActions(false)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-white/5 transition"
                  >
                    <Star size={14} /> Pin Note
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-400 hover:bg-rose-500/10 transition"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Properties Panel */}
        <div className="mb-8">
          <button
            onClick={() => setShowProperties(!showProperties)}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300 transition"
          >
            <ChevronRight size={14} className={cn('transition-transform', showProperties && 'rotate-90')} />
            Properties
          </button>
          {showProperties && (
            <div className="mt-2 text-sm text-zinc-400 grid grid-cols-[100px_1fr] gap-2 pl-5">
              <div className="text-zinc-500">Created</div>
              <div>{formatDate(note.created_at)}</div>
              <div className="text-zinc-500">Updated</div>
              <div>{formatDate(note.updated_at)}</div>
              {note.folder_id && (
                <>
                  <div className="text-zinc-500">Folder</div>
                  <div>{folders.find(f => f.id === note.folder_id)?.name || 'Unknown'}</div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Block Editor */}
        <BlockEditor
          blocks={blocks}
          onChange={setBlocks}
          onCreatePage={handleCreatePage}
        />

        {/* Sub Pages Section */}
        {childPages.length > 0 && (
          <div className="mt-12 pt-6 border-t border-border/40">
            <h3 className="text-lg font-medium text-zinc-300 mb-4">Sub Pages</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {childPages.map(child => (
                <button
                  key={child.id}
                  onClick={() => onNavigate?.(child.id)}
                  className="flex items-center gap-2 p-3 rounded-lg border border-border/60 hover:bg-white/5 hover:border-zinc-600 transition text-left"
                >
                  <span className="text-2xl">{child.icon}</span>
                  <span className="truncate text-zinc-300 font-medium">{child.title || 'Untitled'}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoteEditor;
